import React from 'react';
import { Minus, Edit2, Save, Trash2 } from 'lucide-react';
import ThemedCard from '../../themed/ThemedCard';
import { Table, Guest } from '../types';

interface TableCardProps {
  table: Table;
  editingTable: number | null;
  setEditingTable: (id: number | null) => void;
  getLatestGuestCount: (guest: Guest) => number;
  getLatestGuestGroup: (guest: Guest) => string;
  onUpdateTableName: (tableId: number, newName: string) => void;
  onDeleteTable: (tableId: number) => void;
  onRemoveGuest: (tableId: number, guestId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (tableId: number) => void;
  onTableGuestDragStart: (guest: Guest, fromTableId: number) => void;
}

export const TableCard: React.FC<TableCardProps> = ({
  table,
  editingTable,
  setEditingTable,
  getLatestGuestCount,
  getLatestGuestGroup,
  onUpdateTableName,
  onDeleteTable,
  onRemoveGuest,
  onDragOver,
  onDrop,
  onTableGuestDragStart,
}) => {
  return (
    <ThemedCard
      className="p-4"
      onDragOver={onDragOver}
      onDrop={() => onDrop(table.id)}
    >
      <div className="flex items-center justify-between mb-4">
        {editingTable === table.id ? (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              defaultValue={table.name}
              className="border border-wedding-pink/30 rounded px-2 py-1 text-sm"
              onBlur={e => onUpdateTableName(table.id, e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  onUpdateTableName(table.id, e.currentTarget.value);
                }
              }}
              autoFocus
            />
            <Save
              className="h-4 w-4 cursor-pointer text-wedding-pink"
              onClick={() => setEditingTable(null)}
            />
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <h3 className="font-medium text-wedding-text-title">{table.name}</h3>
            <Edit2
              className="h-4 w-4 cursor-pointer text-wedding-text-body hover:text-wedding-pink"
              onClick={() => setEditingTable(table.id)}
            />
          </div>
        )}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-wedding-text-body">
            {table.guests.reduce((sum, guest) => sum + getLatestGuestCount(guest), 0)}/{table.capacity}
          </span>
          <button
            onClick={() => onDeleteTable(table.id)}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="min-h-[100px] bg-wedding-pink/10 rounded-lg p-2">
        {table.guests.length > 0 ? (
          <div className="space-y-1">
            {table.guests.map((guest, index) => (
              <div
                key={guest.id || `guest-${index}`}
                draggable
                onDragStart={() => onTableGuestDragStart(guest, table.id)}
                className="flex items-center justify-between bg-wedding-bg p-2 rounded text-sm cursor-move hover:bg-wedding-pink/10"
              >
                <div>
                  <p className="font-medium text-wedding-text-title">{guest.name}</p>
                  <p className="text-xs text-wedding-text-body">{getLatestGuestGroup(guest)}</p>
                  {(() => {
                    const latestCount = getLatestGuestCount(guest);
                    return (
                      latestCount > 1 && (
                        <p className="text-xs text-wedding-pink">共 {latestCount} 人</p>
                      )
                    );
                  })()}
                </div>
                <button
                  onClick={() => onRemoveGuest(table.id, guest.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Minus className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-wedding-text-body">
            拖放賓客至此處
          </div>
        )}
      </div>
    </ThemedCard>
  );
};
